// Events — per-performance event analytics. Phase C: reads /events plus
// /productions + /venues for label/lookup. EncorePro is the source of truth;
// every field comes from the canonical Event binding.
//
// Field renames vs. MOCK.EVENTS:
//   e.datetime          → e.performance_at (ISO)
//   e.venue (name)      → resolved via e.venue_id + /venues
//   e.seats             → e.capacity        (canonical "capacity" is seat count)
//   e.capacity (0..1)   → derived            (tickets_sold / capacity)
//   e.revenue (number)  → e.revenue_cents / 100
//   e.avg_ticket        → derived            (revenue_cents / tickets_sold / 100)
//   e.expected_pct      → not on canonical; the UI instead uses the live
//                          production-level /insights/events-pace momentum read
//   e.days_until        → derived from performance_at
//   e.type (mat/eve)    → derived from hour (>= 17 → evening, else matinee)
//   e.note              → not on canonical   (drop)
//   e.source            → not on canonical (this route is EncorePro/API-only)
//   e.added_by          → not on canonical   (drop)
//   e.discount_breakdown→ e.ticket_breakdown ({ general, vip, comp }; the drawer shows general and comp)
//   e.status uppercase  → canonical lowercase ('sold_out', 'on_sale', 'soft', 'completed', 'cancelled')

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icons, fmt, MoneyCell } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import { useEvents, useEventsPace, useProductions, useVenues } from '@/api/hooks';
import { bandColor, bandFor, useSystemMetric } from '@/lib/system-metrics';

import { downloadCsv, csvFilename } from '@/lib/csv';
import { fmtDate, fmtDateShort, fmtHM, nyParts, nyStartOfDayMs, withEt } from '@/lib/datetime';
// The window derivation is shared with this route's LOADER. `eventsList` has no
// `keyOf`, so the query object IS the cache key — a loader computing it any
// other way warms a slot this screen never reads.
import { EVENTS_SCREEN_DEFAULT_RANGE, eventsRangeQuery } from '@/lib/event-ranges';
import type { EventRange } from '@/lib/event-ranges';
import { venueOptionLabel } from '@/lib/venue-label';
import { SourcePill, PosterChip } from './productions';
import { PageActions } from '@/components/page-actions';
import type { Event, EventStatus, EventsPaceResponse, Production, TicketBreakdown, Venue } from '@/api/types';

/** One production's sales-momentum read from `/insights/events-pace`. */
type EventsPaceRow = EventsPaceResponse['productions'][number];
/** The live capacity metric the fill bands are read against (absent until health loads). */
type CapacityMetric = ReturnType<typeof useSystemMetric>;
/** Whether the pace read landed, is in flight, or failed. */
type MomentumState = 'ready' | 'loading' | 'unavailable';
type PerformanceType = 'evening' | 'matinee';
/** One KPI cell: label, value, optional sub-line, and whether the sub-line is a rich block. */
type KpiCell = [label: string, value: ReactNode, sub?: ReactNode, isRich?: boolean];

interface StatusMeta { label: string; color: string; dot: string }

const STATUS_ON_SALE: StatusMeta = { label: 'ON SALE', color: 'var(--ink-1)', dot: 'var(--ink-2)' };

// `unknown` has no entry and renders as ON SALE.
const EVENT_STATUS_META: Partial<Record<EventStatus, StatusMeta>> = {
  sold_out:  { label: 'SOLD OUT', color: 'var(--good)', dot: 'var(--good)' },
  on_sale:   STATUS_ON_SALE,
  soft:      { label: 'SOFT',     color: 'var(--warn)', dot: 'var(--warn)' },
  completed: { label: 'COMPLETED',color: 'var(--ink-3)', dot: 'var(--ink-3)' },
  cancelled: { label: 'CANCELLED',color: 'var(--bad)',  dot: 'var(--bad)'  },
};

function EventStatusPill({ status }: { status: EventStatus }) {
  const m = EVENT_STATUS_META[status] || STATUS_ON_SALE;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6,fontFamily:'var(--font-mono)',fontSize:10.5,fontWeight:600,letterSpacing:'.04em',color: m.color}}>
      <span style={{
        width:7,height:7,borderRadius:'50%',background: m.dot,
        boxShadow: status === 'sold_out' ? '0 0 0 2px color-mix(in oklch, var(--good) 25%, transparent)' : 'none',
      }}/>
      {m.label}
    </span>
  );
}

function MatineePill({ type }: { type: PerformanceType }) {
  const isMat = type === 'matinee';
  return (
    <span style={{
      fontFamily:'var(--font-mono)',fontSize:9,letterSpacing:'.08em',fontWeight:700,
      padding:'1px 5px',borderRadius:2,
      background: isMat ? 'color-mix(in oklch, var(--flag) 16%, transparent)' : 'color-mix(in oklch, var(--ink-2) 12%, transparent)',
      color: isMat ? 'var(--flag)' : 'var(--ink-2)',
      border: '1px solid ' + (isMat ? 'color-mix(in oklch, var(--flag) 35%, transparent)' : 'var(--line)'),
      textTransform:'uppercase',
    }}>{isMat ? 'MAT' : 'EVE'}</span>
  );
}

function eventCapColor(c: number, metric: CapacityMetric) {
  return metric && !metric.stale
    ? bandColor(bandFor(metric, c * 100, 'unknown'))
    : 'var(--ink-3)';
}

function fmtEventDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return fmtDate(iso); // NY instant → "Jul 14, 2026"
}

function fmtEventTime(iso: string | null | undefined) {
  if (!iso) return '—';
  return fmtHM(iso); // NY wall clock → "14:05"
}

function performanceType(iso: string | null | undefined): PerformanceType {
  if (!iso) return 'evening';
  return nyParts(iso).hour >= 17 ? 'evening' : 'matinee';
}

function daysUntilFrom(iso: string | null | undefined) {
  if (!iso) return 0;
  // NY-calendar-day delta so "today"/"tomorrow" track the NY day, not the
  // viewer's local day. Both endpoints are NY midnights (UTC ms); Math.round
  // absorbs the ±1h DST drift between them.
  return Math.round((nyStartOfDayMs(iso) - nyStartOfDayMs(Date.now())) / 86400000);
}

function daysUntilLabel(daysUntil: number, iso: string) {
  if (daysUntil < 0) {
    if (daysUntil === -1) return 'yesterday';
    return Math.abs(daysUntil) + 'd ago';
  }
  if (daysUntil === 0) return 'today';
  if (daysUntil === 1) return 'tomorrow';
  if (daysUntil <= 6) return fmtDateShort(iso); // NY date (no weekday helper) → "Jul 14"
  return 'in ' + daysUntil + 'd';
}

function relMinShort(mins: number | null | undefined) {
  if (mins == null) return '—';
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  return Math.round(mins / 60) + 'h ago';
}

// `state`: 'ready' (the pace read landed) | 'loading' | 'unavailable'. A read
// still in flight must not be reported as unavailable — that is a claim about
// the server, not about the request.
function MomentumCell({ momentum, state }: { momentum: EventsPaceRow | undefined; state: MomentumState }) {
  if (!momentum) {
    return (
      <span className="muted mono" style={{fontSize:11}}>
        {state === 'ready' ? 'no recent sales' : state === 'loading' ? '—' : 'unavailable'}
      </span>
    );
  }
  const last = Number(momentum.sales_last_hour) || 0;
  const prev = Number(momentum.sales_prev_hour) || 0;
  const delta = Number(momentum.delta_pct) || 0;
  const up = last > prev;
  const down = last < prev;
  const c = up ? 'var(--good)' : down ? 'var(--warn)' : 'var(--ink-3)';
  const label = prev === 0 && last > 0 ? `new · ${last}` : `${up ? '▲' : down ? '▼' : '•'} ${Math.abs(delta).toFixed(0)}%`;
  return (
    <span className="mono" title={`${last} sales last hour · ${prev} previous hour`} style={{fontSize:11.5,fontWeight:600,color:c,fontVariantNumeric:'tabular-nums'}}>{label}</span>
  );
}

function CapacityEventCell({ pct, metric }: { pct: number | null; metric: CapacityMetric }) {
  if (pct == null || !isFinite(pct)) return <span className="muted">—</span>;
  const rounded = Math.round(pct * 100);
  const c = eventCapColor(pct, metric);
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,minWidth:120}}>
      <div style={{flex:1,height:5,background:'var(--bg-sunk)',borderRadius:1,overflow:'hidden'}}>
        <div style={{width: rounded + '%',height:'100%',background: c}}/>
      </div>
      <span className="mono" style={{fontSize:11.5,fontWeight:700,color: c,width:34,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{rounded}%</span>
    </div>
  );
}

function EventsKpiStrip({ rows, capM }: { rows: readonly Event[]; capM: CapacityMetric }) {
  const total = rows.length;
  const sold = rows.reduce((a, r) => a + (r.tickets_sold || 0), 0);
  const revenue = rows.reduce((a, r) => a + ((r.revenue_cents || 0) / 100), 0);
  // Weighted fill = Σ sold ÷ Σ capacity over seated events (same method as Productions + Venues).
  const capRows = rows.filter(r => r.capacity > 0);
  const capSeatSum = capRows.reduce((a, r) => a + r.capacity, 0);
  const avgCap = capSeatSum > 0 ? capRows.reduce((a, r) => a + (r.tickets_sold || 0), 0) / capSeatSum : null;
  const soldOut = rows.filter(r => r.status === 'sold_out').length;
  const soft = rows.filter(r => r.status === 'soft').length;
  const softColor = soft === 0 ? 'var(--good)' : 'var(--warn)';
  const capBand = avgCap == null || !capM || capM.stale
    ? 'unknown'
    : bandFor(capM, avgCap * 100, 'unknown');

  // Rich composition card (matches the Productions/Venues KPI cards: a summary
  // line + a 5px two-segment bar) so all three KPI strips share one height.
  const soPct = total ? (soldOut / total) * 100 : 0;
  const soldOutSub = (
    <>
      <div style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2,display:'flex',justifyContent:'space-between'}}>
        <span><span style={{color:'var(--good)'}}>● </span>{soldOut} sold-out</span>
        <span><span style={{color:'var(--ink-3)'}}>● </span>{total - soldOut} open</span>
      </div>
      <div style={{display:'flex',height:5,background:'var(--bg-sunk)',borderRadius:1,overflow:'hidden',marginTop:4}}>
        <div style={{width: soPct + '%',background:'var(--good)'}}/>
        <div style={{width: (100 - soPct) + '%',background:'var(--ink-3)'}}/>
      </div>
    </>
  );

  const cells: KpiCell[] = [
    ['Performances',  fmt.nKpi(total)],
    ['Avg capacity',  avgCap != null ? Math.round(avgCap * 100) + '%' : '—', avgCap != null ? <span style={{color: bandColor(capBand)}}>{capBand === 'ok' ? 'on target' : capBand === 'warn' ? 'on watch' : capBand === 'critical' ? 'below target' : 'threshold unavailable'}</span> : null],
    ['Tickets sold',  fmt.nKpi(sold)],
    ['Sold-out',      fmt.nKpi(soldOut), soldOutSub, true],
    ['Soft events',   <span style={{color: softColor}}>{fmt.nKpi(soft)}</span>],
    ['Window gross',  <MoneyCell value={revenue} dp={2}/>],
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip" style={{gridTemplateColumns:'repeat(6, 1fr)'}}>
      {cells.map(([k, v, sub, isRich]) => (
        <div className="kpi" key={k}>
          <div className="kpi-label">{k}</div>
          <div className="kpi-val">{v}</div>
          {sub && !isRich && <div className="kpi-sub" style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
          {sub && isRich && sub}
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

// Paid (`general`) and free (`comp`) tickets, as the EncorePro rollup fills
// them. VIP is not shown: `tb_vip` has no source (the backfill leaves it 0), so
// it always read "0 · 0%". Every count is a non-null number defaulting to 0, so
// "no mix recorded" is general and comp both 0; the drawer then says so instead
// of drawing a bar of zeros under a non-zero tickets-sold figure.
function ticketMixRecorded(breakdown: TicketBreakdown): boolean {
  return (breakdown.general || 0) !== 0 || (breakdown.comp || 0) !== 0;
}

function DiscountStackBar({ breakdown }: { breakdown: TicketBreakdown }) {
  const order: Array<[label: string, count: number, color: string]> = [
    ['General', breakdown.general || 0, 'var(--good)'],
    ['Comp',    breakdown.comp || 0,    'var(--ink-3)'],
  ];
  const total = order.reduce((a, [, n]) => a + n, 0) || 1;
  return (
    <>
      <div style={{display:'flex',height:10,background:'var(--bg-sunk)',borderRadius:2,overflow:'hidden'}}>
        {order.map(([label, n, color]) => (
          <div key={label} title={`${label}: ${n}`} style={{width: (n / total) * 100 + '%',background: color}}/>
        ))}
      </div>
      <div className="pair-phone" style={{display:'grid',gridTemplateColumns:'repeat(2, 1fr)',gap:6,marginTop:8,fontSize:11}}>
        {order.map(([label, n, color]) => (
          <div key={label}>
            <div className="muted mono" style={{fontSize:9.5,letterSpacing:'.06em',textTransform:'uppercase',display:'inline-flex',alignItems:'center',gap:4}}>
              <span style={{width:6,height:6,borderRadius:1,background: color,display:'inline-block'}}/>
              {label}
            </div>
            <div className="mono" style={{fontWeight:700,marginTop:2}}>{n}</div>
            <div className="mono muted" style={{fontSize:10}}>{Math.round((n / total) * 100)}%</div>
          </div>
        ))}
      </div>
    </>
  );
}

interface EventDetailDrawerProps {
  event: Event | null | undefined;
  production: Production | undefined;
  venue: Venue | undefined;
  momentum: EventsPaceRow | undefined;
  momentumState: MomentumState;
  momentumAsOf: string | undefined;
  onClose: () => void;
}

function EventDetailDrawer({ event, production, venue, momentum, momentumState, momentumAsOf, onClose }: EventDetailDrawerProps) {
  if (!event) return null;
  const isPast = event.status === 'completed';
  const cancelled = event.status === 'cancelled';
  const ptype = performanceType(event.performance_at);
  const capPct = event.capacity ? (event.tickets_sold || 0) / event.capacity : null;
  const avgTicket = event.tickets_sold ? ((event.revenue_cents || 0) / event.tickets_sold) / 100 : 0;
  const venueName = venue?.name || event.venue_id;
  return (
    <>

      <ResponsiveOverlay label="events details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1,minWidth:0,display:'flex',gap:14}}>
            {production && <PosterChip production={production} size={56}/>}
            <div style={{minWidth:0,flex:1}}>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Event · <span style={{color:'var(--ink-1)'}}>{event.id}</span></div>
              <h2 style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,letterSpacing:'.01em',margin:'4px 0 6px',textTransform:'uppercase'}}>{production?.name || event.production_id}</h2>
              <div className="flex items-center gap-2" style={{flexWrap:'wrap'}}>
                <EventStatusPill status={event.status}/>
                <MatineePill type={ptype}/>
                <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>{venueName} · {fmtEventDate(event.performance_at)}, {fmtEventTime(event.performance_at)}</span>
              </div>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          <div className="field-lbl mb-2">At a glance</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="pair-phone" style={{display:'grid',gridTemplateColumns:'repeat(5, 1fr)',gap:14}}>
              {([
                ['Tickets sold',  fmt.n(event.tickets_sold || 0), (event.capacity || 0).toLocaleString() + ' seats'],
                ['Capacity',      capPct != null ? Math.round(capPct * 100) + '%' : '—', null],
                ['Revenue',       <MoneyCell value={(event.revenue_cents || 0) / 100}/>, null],
                ['Avg ticket',    <MoneyCell value={avgTicket}/>, production?.avg_price_cents != null ? '$' + (production.avg_price_cents / 100).toFixed(0) + ' list' : null],
                ['Production sales momentum', <MomentumCell momentum={momentum} state={momentumState}/>, momentumAsOf ? `as of ${withEt(fmtHM(momentumAsOf))}` : momentumState === 'loading' ? 'loading live read…' : 'live read unavailable'],
              ] satisfies Array<[label: string, value: ReactNode, sub: string | null]>).map(([k, v, sub]) => (
                <div key={k}>
                  <div className="muted mono" style={{fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase'}}>{k}</div>
                  <div className="mono" style={{fontSize:17,fontWeight:700,marginTop:2}}>{v}</div>
                  {sub && <div className="mono" style={{fontSize:10,color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
                </div>
              ))}
            </div>
          </div>

          {!cancelled && event.daily_sales_14d && event.daily_sales_14d.length > 0 && (
            <>
              <div className="field-lbl mb-2">Booking pace · last 14 days</div>
              <div className="panel" style={{padding:'14px 14px 8px',marginBottom:14}}>
                <div style={{display:'flex',alignItems:'flex-end',gap:4,height:80}}>
                  {event.daily_sales_14d.map((v, i) => {
                    const max = Math.max(...event.daily_sales_14d) || 1;
                    const h = (v / max) * 100;
                    const isToday = i === event.daily_sales_14d.length - 1 && !isPast;
                    return (
                      <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-end',height:'100%'}}>
                        <div title={`${v} tickets`} style={{
                          width:'100%',height: h + '%',
                          background: isToday ? 'var(--good)' : 'color-mix(in oklch, var(--good) 38%, transparent)',
                          minHeight: 2,borderRadius:'1px 1px 0 0',
                        }}/>
                      </div>
                    );
                  })}
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontFamily:'var(--font-mono)',fontSize:9,letterSpacing:'.06em',color:'var(--ink-3)',textTransform:'uppercase'}}>
                  <span>14 days ago</span>
                  <span>{isPast ? 'event day' : 'today'}</span>
                </div>
              </div>

              {event.ticket_breakdown && (
                <>
                  <div className="field-lbl mb-2">Ticket source mix</div>
                  <div className="panel" style={{padding:12,marginBottom:14}}>
                    {ticketMixRecorded(event.ticket_breakdown)
                      ? <DiscountStackBar breakdown={event.ticket_breakdown}/>
                      : <div className="muted" style={{fontSize:11.5}}>No ticket mix recorded</div>}
                  </div>
                </>
              )}
            </>
          )}

          <div className="field-lbl mb-2">Performance details</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div style={{display:'grid',gridTemplateColumns:'140px 1fr',rowGap:6,fontSize:12}}>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Production</div>
              <div>{production?.name || event.production_id}</div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Venue</div>
              <div>{venueName}</div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Date</div>
              <div className="mono">{withEt(fmtEventDate(event.performance_at) + ' · ' + fmtEventTime(event.performance_at))}</div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Performance type</div>
              <div><MatineePill type={ptype}/></div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>House size</div>
              <div className="mono">{(event.capacity || 0).toLocaleString()} seats</div>
              {!isPast && (
                <>
                  <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Last sale</div>
                  <div className="mono">{relMinShort(event.last_sale_min_ago)}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="drawer-foot">
          <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>API-synced · read-only</span>
          <button className="btn btn-sm" style={{marginLeft:'auto'}} onClick={onClose}>Close</button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// Manual event creation was removed deliberately: the schedule mirrors
// EncorePro (nightly + on-demand backfill upserts) and there is no POST /events
// route. If one-off manual performances return as a feature, restore the
// modal from git history alongside the server route.

const ROW_H = 56;
const { Table } = createDataTable([
  { key: 'production', header: 'Production',  width: 300, mobile: 'title' },
  { key: 'source',     header: 'Source',      width: 100, priority: 3 },
  { key: 'when',       header: 'When (ET)',   width: 200, mobile: 'subtitle' },
  { key: 'status',     header: 'Status',      width: 130, mobile: 'status' },
  { key: 'tickets',    header: 'Tickets',     width: 130, align: 'num', mobile: 'stat' },
  { key: 'capacity',   header: 'Capacity',    width: 180, priority: 2, mobile: 'stat' },
  { key: 'revenue',    header: 'Revenue',     width: 130, align: 'num', mobile: 'stat' },
  { key: 'avg',        header: 'Avg ticket',  width: 110, align: 'num', priority: 3 },
  { key: 'momentum',   header: 'Production momentum · 1h', width: 110, align: 'num', priority: 3,
    title: 'Production-wide net ticket sales in the last hour compared with the previous hour; repeated for performances of the same production' },
  { key: 'daysout',    header: 'Days out',    width: 130, priority: 2, mobile: 'stat' },
  { key: 'lastsale',   header: 'Last sale',   width: 110, priority: 3 },
]);

// `dateRange` is a PROP, not state, and the difference is the whole bug class.
// The window selects the cache key (`eventsList` has no `keyOf`, so the query
// object IS the key), and a key that only exists once the component has mounted
// is unreachable from the loader that runs before it. As state, the loader could
// only guess — and it guessed `{}`, a slot nothing reads. From the URL, the
// loader and the screen read the same value and cannot disagree.
interface EventsViewProps {
  setView?: (viewId: string) => void;
  focus?: string;
  dateRange: EventRange;
  onDateRangeChange: (next: EventRange) => void;
}

function EventsView({ setView: _setView, focus, dateRange, onDateRangeChange }: EventsViewProps) {
  const eventsQuery = useMemo(() => eventsRangeQuery(dateRange), [dateRange]);
  const eventsResult = useEvents(eventsQuery);
  const events = eventsResult.data ?? [];
  const paceQ = useEventsPace();
  const momentumState: MomentumState = paceQ.data ? 'ready' : paceQ.isError ? 'unavailable' : 'loading';
  const paceByProduction = useMemo(
    () => Object.fromEntries((paceQ.data?.productions ?? []).map(row => [row.production_id, row])),
    [paceQ.data],
  );
  const capM = useSystemMetric('events.avg_capacity_pct');
  const { data: productions = [] } = useProductions();
  const { data: venues = [] } = useVenues();
  const productionMap = useMemo(() => Object.fromEntries(productions.map(p => [p.id, p])), [productions]);
  const venueMap = useMemo(() => Object.fromEntries(venues.map(v => [v.id, v])), [venues]);

  // Filters (date window is applied server-side via eventsQuery above)
  const [productionFilter, setProductionFilter] = useState<string>('all');
  const [venueFilter,  setVenueFilter]  = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');
  const [q,            setQ]            = useState('');
  const [selected,     setSelected]     = useState<Event | null>(null);
  // Deep-link ?focus=<event id> opens that event's drawer (best-effort: only if
  // the event is within the loaded window).
  useEffect(() => {
    if (focus) { const ev = events.find(e => e.id === focus); if (ev) setSelected(ev); }
  }, [focus, events]);

  const sorted = useMemo(
    () => [...events].sort((a, b) => +new Date(a.performance_at || 0) - +new Date(b.performance_at || 0)),
    [events]
  );

  const rows = sorted.filter(e => {
    if (productionFilter !== 'all' && e.production_id !== productionFilter) return false;
    if (venueFilter !== 'all' && e.venue_id !== venueFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      const p = productionMap[e.production_id];
      const v = venueMap[e.venue_id];
      const hay = (p?.name || '') + ' ' + (v?.name || '') + ' ' + e.id;
      if (!hay.toLowerCase().includes(needle)) return false;
    }
    return true;
  });


  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Events</h1>
        </div>
        <PageActions>
          <span className="muted mono" data-phone="hidden" title="The schedule mirrors EncorePro (nightly + on-demand backfill); events are read-only here." style={{fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', alignSelf:'center', marginRight:4}}>
            ⟳ Synced from EncorePro
          </span>
          <button className="btn btn-icon-phone" data-phone="inline" aria-label="Export CSV" onClick={() => downloadCsv(csvFilename('events'), rows.map(ev => {
            const p = productionMap[ev.production_id];
            const v = venueMap[ev.venue_id];
            const momentum = paceByProduction[ev.production_id];
            return {
              'Production': p?.name || ev.production_id,
              'Venue': v?.name || ev.venue_id,
              'Event ID': ev.id,
              'Performance at': ev.performance_at,
              'Status': ev.status,
              'Tickets sold': ev.tickets_sold ?? 0,
              'Capacity': ev.capacity ?? '',
              'Revenue $': ((ev.revenue_cents || 0) / 100).toFixed(2),
              'General': ev.ticket_breakdown?.general ?? '',
              'VIP': ev.ticket_breakdown?.vip ?? '',
              'Comp': ev.ticket_breakdown?.comp ?? '',
              'Last sale (min ago)': ev.last_sale_min_ago ?? '',
              'Sales last hour': momentum?.sales_last_hour ?? '',
              'Sales previous hour': momentum?.sales_prev_hour ?? '',
              'Sales momentum %': momentum?.delta_pct ?? '',
            };
          }))}><span>{Icons.dl}</span><span className="phone-long">Export CSV</span></button>
        </PageActions>
      </div>

      <EventsKpiStrip rows={rows} capM={capM}/>

      {paceQ.isError && (
        <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
            ⚠ Sales momentum unavailable — event facts remain available. Retrying automatically.
          </span>
        </div>
      )}

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0'}}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter events by production or venue"/>
        </div>
        <Dropdown value={dateRange} options={[
          { id:'today',     label:'Today' },
          { id:'this_week', label:'This week' },
          { id:'next7',     label:'Next 7 days' },
          { id:'past_week', label:'Past week' },
        ]} onChange={onDateRangeChange} label="Window" data-filter-default={EVENTS_SCREEN_DEFAULT_RANGE}/>
        <Dropdown value={productionFilter} options={[
          { id:'all', label:'All productions' },
          ...productions.slice().sort((a,b) => (a.name || '').localeCompare(b.name || '')).map(p => ({ id: p.id, label: p.name })),
        ]} onChange={setProductionFilter} label="Production" width={220}/>
        <Dropdown value={venueFilter} options={[
          { id:'all', label:'All venues' },
          ...venues.map(v => ({ id: v.id, label: venueOptionLabel(v) })),
        ]} onChange={setVenueFilter} label="Venue" width={200}/>
        <Dropdown value={statusFilter} options={[
          { id:'all',       label:'All statuses' },
          { id:'sold_out',  label:'Sold-out' },
          { id:'on_sale',   label:'On sale' },
          { id:'soft',      label:'Soft' },
          { id:'completed', label:'Completed' },
          { id:'cancelled', label:'Cancelled' },
        ]} onChange={setStatusFilter} label="Status"/>
      </ResponsiveFilters>

      <Table
        rows={rows}
        rowH={ROW_H}
        unfilteredTotal={events.length}
        deps={[{ what: 'events', query: eventsResult }]}
        empty="No events in this window. They sync in from EncorePro automatically."
        noMatch="No events match these filters."
        onRowClick={setSelected}
        rowStyle={(ev) => (ev.status === 'completed' ? { opacity: 0.65 } : undefined)}
        renderRow={(ev) => {
          const p = productionMap[ev.production_id];
          const v = venueMap[ev.venue_id];
          const isPast = ev.status === 'completed';
          const capPct = ev.capacity ? (ev.tickets_sold || 0) / ev.capacity : null;
          const avgTicket = ev.tickets_sold ? ((ev.revenue_cents || 0) / ev.tickets_sold) / 100 : 0;
          const momentum = paceByProduction[ev.production_id];
          const days = daysUntilFrom(ev.performance_at);
          return (
            <>
              <td className="name" style={{lineHeight:1.25}}>
                <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0,flex:1}}>
                  {p && <PosterChip production={p} size={32}/>}
                  <div style={{minWidth:0,flex:1,overflow:'hidden'}}>
                    <div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{p?.name || ev.production_id}</div>
                    <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',fontWeight:400,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {v?.name || ev.venue_id}
                    </div>
                  </div>
                </div>
              </td>
              <td><SourcePill source="api"/></td>
              <td className="mono" style={{fontSize:11.5,lineHeight:1.2}}>
                <div style={{display:'flex',flexDirection:'column',minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontWeight:600}}>{fmtEventDate(ev.performance_at)}</span>
                    <MatineePill type={performanceType(ev.performance_at)}/>
                  </div>
                  <div className="muted mono" style={{fontSize:11,marginTop:1}}>{fmtEventTime(ev.performance_at)}</div>
                </div>
              </td>
              <td><EventStatusPill status={ev.status}/></td>
              <td className="num mono" style={{fontVariantNumeric:'tabular-nums',lineHeight:1.2}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end'}}>
                  <span>{(ev.tickets_sold || 0).toLocaleString()}</span>
                  <span className="muted mono" style={{fontSize:10}}>/ {ev.capacity ? ev.capacity.toLocaleString() : '—'}</span>
                </div>
              </td>
              <td><CapacityEventCell pct={capPct} metric={capM}/></td>
              <td className="num"><MoneyCell value={(ev.revenue_cents || 0) / 100}/></td>
              <td className="num"><MoneyCell value={avgTicket}/></td>
              <td className="num"><MomentumCell momentum={momentum} state={momentumState}/></td>
              <td className="mono" style={{fontSize:11,color: days === 0 && !isPast ? 'var(--accent)' : 'var(--ink-2)', fontWeight: days === 0 && !isPast ? 700 : 400}}>{daysUntilLabel(days, ev.performance_at)}</td>
              <td className="mono" style={{fontSize:11,color:'var(--ink-2)'}}>{relMinShort(ev.last_sale_min_ago)}</td>
            </>
          );
        }}
      />

      {selected && <EventDetailDrawer event={selected} production={productionMap[selected.production_id]} venue={venueMap[selected.venue_id]} momentum={paceByProduction[selected.production_id]} momentumState={momentumState} momentumAsOf={paceQ.data?.as_of} onClose={() => setSelected(null)}/>}
    </div>
  );
}

export { EventsView, EventDetailDrawer, EventStatusPill };
